// UPost - Referral Program Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Logo } from '../components/Logo';
import { 
  Users, DollarSign, Copy, Share2, Mail, MessageCircle,
  TrendingUp, Gift, Award, CheckCircle, ArrowLeft, Loader2 
} from 'lucide-react';

export function ReferralDashboard() {
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadReferralData();
  }, []);

  async function loadReferralData() {
    try {
      const [codeData, statsData, referralsList] = await Promise.all([
        api.request('/referrals/my-code'),
        api.request('/referrals/stats'),
        api.request('/referrals/list')
      ]);

      setReferralCode(codeData.referral_code);
      setReferralLink(codeData.referral_link);
      setStats(statsData);
      setReferrals(referralsList.referrals);
    } catch (err) {
      console.error('Failed to load referral data:', err);
    } finally {
      setLoading(false);
    }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function shareViaEmail() {
    const subject = encodeURIComponent('Try UPost - AI Writing Platform');
    const body = encodeURIComponent(
      `I've been using UPost for my writing and thought you'd love it!\n\n` +
      `It's an AI-powered platform for writing novels, screenplays, and manuscripts.\n\n` +
      `Sign up with my referral link and get started:\n${referralLink}\n\n` +
      `Happy writing!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  }

  function shareOnTwitter() {
    const text = encodeURIComponent(
      `Check out UPost - an amazing AI-powered writing platform for novelists and screenwriters! ${referralLink}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  }

  async function handleWithdraw() {
    const amount = parseFloat(withdrawAmount);
    
    if (isNaN(amount) || amount < 25) {
      alert('Minimum withdrawal amount is $25');
      return;
    }
    
    if (amount > stats.available_balance) {
      alert('Amount exceeds available balance');
      return;
    }

    if (!stats.w9_on_file) {
      alert('Please submit your W-9 tax form before requesting a payout');
      navigate('/w9-form');
      return;
    }

    setWithdrawing(true);
    try {
      await api.request('/referrals/withdraw', {
        method: 'POST',
        body: JSON.stringify({ amount })
      });

      alert(`✅ Payout request submitted!\n\nAmount: $${amount.toFixed(2)}\n\nYou will receive payment via Stripe within 3-5 business days.`);
      setShowPayoutModal(false);
      setWithdrawAmount('');
      loadReferralData(); // Refresh stats
    } catch (err) {
      alert(err.message || 'Failed to request payout');
    } finally {
      setWithdrawing(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link to="/">
              <Logo variant="full" size="md" />
            </Link>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft size={16} className="mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                Log Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
            Referral Program
          </h1>
          <p className="text-gray-600 text-lg">
            Earn recurring commissions and bonus AI words by referring writers to UPost!
          </p>
        </div>

        {/* W-9 Alert */}
        {!stats?.w9_on_file && stats?.available_balance > 0 && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <strong className="text-amber-900">W-9 Tax Form Required</strong>
                  <p className="text-sm text-amber-800 mt-1">
                    Submit your W-9 form to withdraw commission earnings. Required by IRS for payments over $600/year.
                  </p>
                </div>
                <Link to="/w9-form">
                  <Button className="ml-4 bg-amber-600 hover:bg-amber-700">
                    Submit W-9 Now
                  </Button>
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available Balance</p>
                  <p className="text-3xl font-bold text-green-600">
                    ${stats?.available_balance?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {stats?.w9_on_file ? '✓ W-9 on file' : '⚠ W-9 required'}
                  </p>
                </div>
                <DollarSign className="text-green-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Referrals</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {stats?.total_referrals || 0}
                  </p>
                  <p className="text-xs text-gray-500">
                    {stats?.pro_referrals || 0} Pro referrals
                  </p>
                </div>
                <Users className="text-purple-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Bonus Words Earned</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {stats?.bonus_words_earned?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-gray-500">
                    +5,000 per free signup
                  </p>
                </div>
                <Gift className="text-blue-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Earned</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    ${stats?.total_earned?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Lifetime commissions
                  </p>
                </div>
                <TrendingUp className="text-emerald-600" size={40} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Commission Payout Section */}
        <Card className="mb-8 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign size={24} className="text-green-600" />
              Commission Payout
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available Balance</p>
                  <p className="text-4xl font-bold text-green-600">
                    ${stats?.available_balance?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum withdrawal: $25.00
                  </p>
                </div>
                <Button
                  onClick={() => setShowPayoutModal(true)}
                  disabled={!stats?.available_balance || stats.available_balance < 25 || !stats?.w9_on_file}
                  className="bg-green-600 hover:bg-green-700 px-8 py-6 text-lg"
                >
                  💰 Request Payout
                </Button>
              </div>

              {!stats?.w9_on_file && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertDescription className="text-amber-800">
                    <strong>W-9 Required:</strong> Submit your tax form to enable payouts.{' '}
                    <Link to="/w9-form" className="underline font-semibold">
                      Submit W-9 Now →
                    </Link>
                  </AlertDescription>
                </Alert>
              )}

              {stats?.w9_on_file && stats?.available_balance < 25 && (
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-800">
                    <strong>Keep Growing:</strong> You need ${(25 - (stats?.available_balance || 0)).toFixed(2)} more to reach the minimum payout amount.
                  </AlertDescription>
                </Alert>
              )}

              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">💳 Payout Details</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>✓ <strong>Payment Method:</strong> Stripe (direct deposit)</li>
                  <li>✓ <strong>Processing Time:</strong> 3-5 business days</li>
                  <li>✓ <strong>Minimum Amount:</strong> $25.00</li>
                  <li>✓ <strong>Tax Reporting:</strong> W-9 required for payments over $600/year</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Share Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 size={24} />
              Share Your Referral Link
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Referral Code Display */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Referral Code
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 font-mono text-2xl font-bold text-purple-600">
                    {referralCode}
                  </div>
                  <Button
                    onClick={() => copyToClipboard(referralCode)}
                    className="flex items-center gap-2"
                  >
                    {copied ? <CheckCircle size={20} /> : <Copy size={20} />}
                    {copied ? 'Copied!' : 'Copy Code'}
                  </Button>
                </div>
              </div>

              {/* Referral Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Referral Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-sm"
                  />
                  <Button
                    onClick={() => copyToClipboard(referralLink)}
                    variant="outline"
                  >
                    <Copy size={16} className="mr-2" />
                    Copy Link
                  </Button>
                </div>
              </div>

              {/* Share Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share via
                </label>
                <div className="flex gap-3">
                  <Button
                    onClick={shareViaEmail}
                    variant="outline"
                    className="flex-1"
                  >
                    <Mail size={20} className="mr-2" />
                    Email
                  </Button>
                  <Button
                    onClick={shareOnTwitter}
                    variant="outline"
                    className="flex-1"
                  >
                    <MessageCircle size={20} className="mr-2" />
                    Twitter
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Media Kit CTA */}
        <Card className="mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-amber-900 mb-2 flex items-center gap-2">
                  🎨 Free Marketing Materials
                </h3>
                <p className="text-gray-700 mb-3">
                  Get ready-to-use templates for all social media platforms, email, YouTube, and more. 
                  Professional copy and banner size recommendations included!
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>✓ Twitter, Facebook, Instagram templates</li>
                  <li>✓ YouTube video descriptions</li>
                  <li>✓ Email templates & blog post outlines</li>
                  <li>✓ Banner size guide for all platforms</li>
                </ul>
                <Link to="/media-kit">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    View Media Kit →
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block ml-6 text-6xl">
                📱
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Commission Structure */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-medium">Share Your Link</p>
                    <p className="text-sm text-gray-600">
                      Send your referral link to writers, authors, and screenwriters
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-medium">They Sign Up Free</p>
                    <p className="text-sm text-gray-600">
                      You earn +5,000 bonus AI words instantly
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-green-100 text-green-600 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-medium">They Upgrade to Pro</p>
                    <p className="text-sm text-gray-600">
                      Earn $4.75/month recurring commission (25% of $19)
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-amber-100 text-amber-600 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                    4
                  </div>
                  <div>
                    <p className="font-medium">Get Paid Monthly</p>
                    <p className="text-sm text-gray-600">
                      Withdraw to PayPal or use as account credit
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Rewards & Milestones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-blue-900">Quick Win</p>
                    <Award className="text-blue-600" size={20} />
                  </div>
                  <p className="text-sm text-blue-700 mb-1">
                    Refer 3 Pro users OR 15 total users
                  </p>
                  <p className="text-xs text-blue-600">
                    🎁 Reward: 1 Month Free Pro
                  </p>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-purple-900">Power User</p>
                    <Award className="text-purple-600" size={20} />
                  </div>
                  <p className="text-sm text-purple-700 mb-1">
                    Refer 10 Pro users OR 50 total users
                  </p>
                  <p className="text-xs text-purple-600">
                    🎁 Reward: 3 Months Free Pro
                  </p>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-amber-900">Super Affiliate</p>
                    <Award className="text-amber-600" size={20} />
                  </div>
                  <p className="text-sm text-amber-700 mb-1">
                    Refer 25 Pro users OR 150 total users
                  </p>
                  <p className="text-xs text-amber-600">
                    🎁 Reward: 12 Months Free Pro
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referrals List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Referrals ({referrals.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <div className="text-center py-8">
                <Users className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 mb-2">No referrals yet</p>
                <p className="text-sm text-gray-500">
                  Start sharing your referral link to earn commissions and bonus words!
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="pb-2">Name</th>
                      <th className="pb-2">Email</th>
                      <th className="pb-2">Joined</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2">Your Earnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map(ref => (
                      <tr key={ref.id} className="border-b">
                        <td className="py-3">{ref.name}</td>
                        <td className="py-3 text-sm text-gray-600">{ref.email}</td>
                        <td className="py-3 text-sm">
                          {new Date(ref.signup_date).toLocaleDateString()}
                        </td>
                        <td className="py-3">
                          <span className={`text-xs px-2 py-1 rounded ${
                            ref.tier === 'pro' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {ref.tier === 'pro' ? 'PRO ($4.75/mo)' : 'FREE (+5K words)'}
                          </span>
                        </td>
                        <td className="py-3 font-semibold text-green-600">
                          {ref.tier === 'pro' 
                            ? `$${ref.total_commissions?.toFixed(2) || '0.00'}`
                            : `${ref.bonus_words} words`
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Request Commission Payout</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Available Balance: <strong className="text-green-600 text-lg">${stats?.available_balance?.toFixed(2)}</strong>
              </p>
              <p className="text-xs text-gray-500">
                Minimum withdrawal: $25.00 • Processing time: 3-5 business days
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Withdrawal Amount ($)
              </label>
              <input
                type="number"
                min="25"
                max={stats?.available_balance || 0}
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
              />
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWithdrawAmount('25')}
                >
                  $25
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWithdrawAmount('50')}
                >
                  $50
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWithdrawAmount('100')}
                >
                  $100
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWithdrawAmount(stats?.available_balance?.toFixed(2) || '0')}
                >
                  Max
                </Button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-800">
                <strong>Payment Method:</strong> Funds will be sent to your Stripe account within 3-5 business days. 
                W-9 tax form required for payments over $600/year.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPayoutModal(false);
                  setWithdrawAmount('');
                }}
                disabled={withdrawing}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleWithdraw}
                disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) < 25}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {withdrawing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>💰 Request ${parseFloat(withdrawAmount || 0).toFixed(2)}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
